import type { ChatType } from '@1js/scc-types';
import type { GptIdentifer, MailboxInformation, GptDefinition } from '@1js/sydney-types';

// This is the subset of properties on IContextualChat interface that we support
// sending over the bridge to BizChat at this time.
export type ContextualChatChangedAnnotation = Pick<
    IContextualChat,
    'contentType' | 'contentItems' | 'userAction' | 'subContentType' | 'agentModeGptItem'
>;

// https://office.visualstudio.com/Office/_git/1JS?path=/midgard/packages/bizchat-catalyst-types/src/IContextualChat.ts&_a=contents&version=GBmain
export interface IContextualChat {
    displaySummaryPrompt?: boolean;
    currentFileURL?: string;
    isVideoPage?: boolean;
    capabilities?: IBizchatCapability[];
    /**
     * @deprecated Use contentType in BaseContent instead
     * What type to expect in contentItems
     * */
    contentType?: ContentItemType;
    subContentType?: string;
    contentItems?: ContentItem[];
    userAction?: string;
    serverVariants?: string[];
    agentModeGptItem?: ICustomGptItem;
    customGptDefinition?: GptDefinition;
    agentTabUrl?: string;
    hasAgentTab?: boolean;
    browserActionsEligibilityStatus?: string;
    clientFlights3S?: string[]; // List of flight overrides to apply when calling 3SS to fetch prompts
}

// For deeplink handin via subPageId
// Note conversationId is not part of LaunchOptionPromptDataV2 in 1JS, but is spec'd that it can be used
// https://office.visualstudio.com/Office/_wiki/wikis/1JS/118816/BizChat-Deep-Linking-for-Handin-scenarios
export interface LaunchOptionPromptDataV2 {
    id?: string;
    scenario: string;
    properties: {
        promptSource?: string;
        clickTimestamp?: string;
        copilotReferenceIdWillResolve?: boolean;
    };
    version: number;
    chatType?: ChatType;
    hasUsedPrompt?: boolean;
    callerInfo?: CallerInfo;
    referral?: {
        cmmid?: string;
    };
    serverVariants?: string[];
    conversationId?: string;
}

// https://office.visualstudio.com/Office/_git/1JS?path=/midgard/packages/bizchat-catalyst-types/src/PromptInputData.ts&version=GBmain&line=27&lineEnd=32&lineStartColumn=1&lineEndColumn=3&lineStyle=plain&_a=contents
export interface CallerInfo {
    /** Product Category */
    scope?: string;
    /** Product Entry Point */
    entryPoint?: string;
    copilotReferenceId?: string;
    /** Product Entry Point */
    productEntryPoint?: string;
}

export enum ContentItemType {
    EMAIL = 'email',
    CALENDAR_INVITE = 'calendarInvite',
    CANVAS = 'canvas',
    EMAIL_CONVERSATION = 'emailConversation',
}

export interface BaseContent {
    selectionContext?: ISelectionContext;
    contentType?: ContentItemType;
    mailboxInformation?: MailboxInformation;
}

export interface ISelectionContext {
    selectedContentItems?: SelectionContentItem[];
}

export type SelectionContentItem = TextContextItem;

export type TextContextItem = {
    type: 'Text';
    selectedText?: string;
    offset?: number;
};

export interface ICustomGptItemConfig {
    /** Show the Agent in the UI (enable this agent) */
    isAgentEnabled: boolean;

    /** Allow Implicit CIQ */
    allowImplicitCIQ: boolean;

    /** Allow Explicit CIQ */
    allowExplicitCIQ: boolean;

    /** Select the agent when the component is initialized */
    selectAgentOnInit?: boolean;

    /** Disable the Agent (shown but greyed out in the UI) */
    disableAgentSelection?: boolean;

    /** The version of the agent to use */
    agentVersion?: string;

    /** Callback function when the agent is toggled (true = enabled, false = disabled) */
    onToggled?: (state: boolean) => void;
}

export interface ICustomGptItem {
    agentConfig: ICustomGptItemConfig;
    item: GptItem;
}

// Only including properties relevant to our usage. See 1js for the full definition.
export interface GptItem {
    /**
     * Branding `GptItem` and `GptInfo` to force a method to only accept `GptItem` or `GptInfo`, even if they are structurally identical or compatible
     * The most ideal fix would be to make some on-compatible fields non-optional in both `GptItem` and `GptInfo`
     */
    readonly _brand: 'GptItem';
    name?: string;
    gptIdentifier?: GptIdentifer;
}

export interface IBizchatCapability {
    name: SupportedChatCapability;
    /** @deprecated Not needed to disable. */
    disabled?: boolean;
}

export type SupportedChatCapability =
    | 'disableVoiceInput'
    | 'disableRealTimeConversation'
    | 'disableCopy'
    | 'disableInput'
    | 'disableNewChatButton';

export interface PersonWrapper {
    name: string;
    id: string;
}

/** Email content data type */
export interface BaseEmailContent extends BaseContent {
    subject?: string;
    body?: string;
    sender?: PersonWrapper;
    recipients?: string[];
    cc?: string[];
    bcc?: string[];
    attachments?: string[];
    renderedHtml?: string;
    precedingText?: string;
    followingText?: string;
    isDraft?: boolean;
    editorId?: string;
}

export interface ServerEmailContent extends BaseEmailContent {
    id: string;
    receivedTime?: Date;
    sentTime?: Date;
}

export interface DraftEmailContent extends BaseEmailContent {
    responseToEmailId?: string; // Optional, if this is a response to another email
    savedTime?: Date;
}

export interface EmailConversationContent extends BaseContent, CanvasContent {
    id: string;
    conversationTopic: string;
    precedingText?: string;
    followingText?: string;
    isDraft?: boolean;
}

export type EmailContent = ServerEmailContent | DraftEmailContent;

export interface CalendarInviteContent extends BaseContent, CanvasContent {
    id: string;
    title?: string;
    startTime?: Date;
    endTime?: Date;
    location?: string;
    meetingParameters?: string;
    attendees?: string[]; // List of attendee email addresses
    organizer?: string; // Email address of the meeting organizer
    body?: string; // Body of the calendar invite
    attachments?: string[]; // List of attachment file names or URLs
}

export interface CanvasContent extends BaseContent {
    appName?: string;
    module?: string;
    sourceScenario?: string;
    listViewSelection?: string;
}

export type ContentItem =
    | EmailContent
    | CalendarInviteContent
    | CanvasContent
    | EmailConversationContent; // union of interfaces

export interface PrefetchContextAnnotation {
    id: string;
    text: string;
    entityType: 'Message' | 'Event' | 'EmailConversation'; // Must be valid query annotation type
    sender?: PersonWrapper;
}

// #endregion
